import { useMemo, useState } from "react";
import { createPortal } from "react-dom";

const inputClass =
  "w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm text-black placeholder-gray-400 focus:outline-none focus:border-black focus:bg-white transition-all duration-200";

const labelClass =
  "block text-xs font-semibold text-gray-500 uppercase tracking-widest mb-2";

const AddAppModal = ({ open, onClose, onSave }) => {
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [inlineError, setInlineError] = useState("");
  const canSave = useMemo(() => name.trim().length > 0 && !saving, [name, saving]);

  if (!open) return null;

  const handleSave = async () => {
    if (!canSave) return;
    try {
      setSaving(true);
      setInlineError("");
      const result = await onSave?.(name.trim());
      if (result?.success) {
        setName("");
        return;
      }
      setInlineError(result?.message || "Failed to save app");
    } catch (err) {
      setInlineError("Failed to save app");
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div className="absolute inset-0 bg-black/50" />

      <div className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden">
        <div className="bg-black px-8 py-6 flex items-center justify-between">
          <div>
            <h2 className="text-white text-lg font-bold">Add App</h2>
            <p className="text-gray-400 text-xs mt-0.5">
              Enter an app name and save
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-8 py-6 space-y-5">
          <div>
            <label className={labelClass}>App Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => {
                let val = e.target.value;
                if (val.length > 0) {
                  val = val.charAt(0).toUpperCase() + val.slice(1);
                }
                setName(val);
                if (inlineError) setInlineError("");
              }}
              placeholder="Eg: Zomato"
              className={inputClass}
              autoFocus
            />
            {inlineError ? (
              <p className="mt-2 text-xs font-medium text-red-600">{inlineError}</p>
            ) : null}
          </div>
        </div>

        <div className="px-8 py-5 border-t border-gray-100">
          <button
            onClick={handleSave}
            disabled={!canSave}
            className={`w-full py-3.5 rounded-xl font-semibold text-sm tracking-wide transition-all duration-200 ${
              canSave
                ? "bg-blue-600 text-white hover:bg-blue-700 active:scale-[0.98]"
                : "bg-gray-100 text-gray-400 cursor-not-allowed"
            }`}
          >
            {saving ? "Saving..." : "Save"}
          </button>
          <button
            type="button"
            onClick={() => {
              setInlineError("");
              onClose?.();
            }}
            className="w-full mt-3 py-3 rounded-xl text-sm text-gray-500 hover:text-black font-medium border border-gray-200 hover:border-gray-400 transition-all duration-200"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default AddAppModal;

